<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div>
				<h1 class="mb-0">Interventions</h1>
				<p class="text-muted mb-0">
					The physical-control interventions your district records when no
					product was applied.
				</p>
			</div>
		</div>

		<div class="card">
			<div class="card-body">
				<div
					class="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-3"
				>
					<div>
						<h2 class="h5 mb-0">District Interventions</h2>
						<p class="text-muted mb-0 small">
							The district's own non-product (physical-control) interventions. A
							treatment records one of these when no product was applied.
							Planned residuals save automatically.
						</p>
					</div>
					<SaveIndicator
						:state="interventionSaveState"
						:pending="interventionPending"
						@retry="retryInterventionSave"
					/>
				</div>

				<div v-if="interventionsError" class="alert alert-danger" role="alert">
					{{ interventionsError }}
				</div>

				<form
					class="row g-2 align-items-end mb-3"
					@submit.prevent="addIntervention"
				>
					<div class="col-12 col-md-5 col-lg-4">
						<label class="form-label" for="interventionName">Name</label>
						<input
							id="interventionName"
							v-model="newIntervention.name"
							type="text"
							class="form-control"
							placeholder="e.g. Drained Source"
							required
						/>
					</div>
					<div class="col-12 col-md-4 col-lg-3">
						<label class="form-label" for="interventionControlClass">
							Control class
						</label>
						<select
							id="interventionControlClass"
							v-model="newIntervention.controlClass"
							class="form-select"
						>
							<option value="">Not set</option>
							<option
								v-for="option in CONTROL_CLASS_OPTIONS"
								:key="option.value"
								:value="option.value"
							>
								{{ option.label }}
							</option>
						</select>
					</div>
					<div class="col-12 col-md-auto">
						<ButtonLoading
							text="Add"
							icon="bi bi-plus-lg"
							variant="primary"
							:loading="savingIntervention"
							:disabled="newIntervention.name.trim() === ''"
							@click="addIntervention"
						/>
					</div>
				</form>

				<div v-if="interventionsLoading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div
					v-else-if="interventions.length === 0"
					class="text-center py-4 text-muted"
				>
					No interventions yet. Add the district's first one above.
				</div>

				<div v-else class="table-responsive">
					<table class="table table-striped table-hover align-middle">
						<thead class="table-light">
							<tr>
								<th scope="col">Name</th>
								<th scope="col">Control class</th>
								<th scope="col">
									Planned residual (days)
									<div class="text-muted small fw-normal">
										Days the physical control is planned to hold
									</div>
								</th>
								<th scope="col">Status</th>
								<th scope="col" class="text-end">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="intervention in interventions"
								:key="intervention.id"
								:class="{ 'text-muted': intervention.retired }"
							>
								<td class="fw-semibold">{{ intervention.name }}</td>
								<td>{{ controlClassLabel(intervention.control_class) }}</td>
								<td>
									<input
										type="number"
										min="0"
										step="1"
										class="form-control form-control-sm"
										:value="interventionResiduals[intervention.id] ?? ''"
										:aria-label="`Planned residual days for ${intervention.name}`"
										@change="
											updateInterventionResidual(intervention.id, $event)
										"
									/>
								</td>
								<td>
									<span
										class="badge rounded-pill"
										:class="
											intervention.retired ? 'bg-secondary' : 'bg-success'
										"
									>
										{{ intervention.retired ? "Retired" : "Active" }}
									</span>
								</td>
								<td class="text-end text-nowrap">
									<ButtonLoading
										v-if="!intervention.retired"
										text="Retire"
										icon="bi bi-archive"
										variant="outline-secondary"
										size="sm"
										:loading="retiringId === intervention.id"
										@click="retireIntervention(intervention.id)"
									/>
									<span v-else class="text-muted small">—</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";

import ButtonLoading from "@/components/common/ButtonLoading.vue";
import SaveIndicator from "@/components/common/SaveIndicator.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { useSaveState } from "@/lib/autoSave";
import {
	CONTROL_CLASS_LABELS,
	CONTROL_CLASS_OPTIONS,
} from "@/lib/controlClass";
import { useStoreIntervention } from "@/store/intervention";

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const storeIntervention = useStoreIntervention();
const interventions = computed(() => storeIntervention.interventions);
const interventionsLoading = computed(() => storeIntervention.loading);
const interventionsError = ref("");
const {
	state: interventionSaveState,
	pending: interventionPending,
	run: runInterventionSave,
	retry: retryInterventionSave,
} = useSaveState("pesticide-interventions");
const savingIntervention = ref(false);
const retiringId = ref<number | null>(null);
// interventionResiduals holds the in-progress planned residual per intervention
// id, as the input's string value; an intervention with none is the empty
// string. Interventions have no catalog suggestion, so there is no placeholder.
const interventionResiduals = ref<Record<number, string>>({});
const newIntervention = reactive({ name: "", controlClass: "" });

function controlClassLabel(value: string | null): string {
	if (value === null) return "—";
	return CONTROL_CLASS_LABELS[value] ?? value;
}

async function loadInterventions() {
	interventionsError.value = "";
	try {
		const loaded = await storeIntervention.load();
		const residuals: Record<number, string> = {};
		for (const intervention of loaded) {
			residuals[intervention.id] =
				intervention.residual_days !== null
					? String(intervention.residual_days)
					: "";
		}
		interventionResiduals.value = residuals;
	} catch (e: any) {
		interventionsError.value = e?.message || "Failed to load interventions.";
	}
}

async function addIntervention() {
	const name = newIntervention.name.trim();
	if (name === "") return;
	savingIntervention.value = true;
	try {
		await storeIntervention.create(name, newIntervention.controlClass || null);
		newIntervention.name = "";
		newIntervention.controlClass = "";
		interventionsError.value = "";
		showNotification("Intervention saved", `"${name}" is available to record.`);
	} catch (e: any) {
		showNotification(
			"Failed to save intervention",
			e?.message || "Please try again.",
		);
	} finally {
		savingIntervention.value = false;
	}
}

async function retireIntervention(id: number) {
	retiringId.value = id;
	try {
		await storeIntervention.retire(id);
		interventionsError.value = "";
		showNotification(
			"Intervention retired",
			"It stays on past treatments but is no longer offered.",
		);
	} catch (e: any) {
		showNotification(
			"Failed to retire intervention",
			e?.message || "Please try again.",
		);
	} finally {
		retiringId.value = null;
	}
}

// An intervention's planned residual is one field, so it auto-saves on change
// like the district products. An empty box clears it; anything else must be a
// whole, non-negative day count.
async function updateInterventionResidual(id: number, event: Event) {
	const raw = (event.target as HTMLInputElement).value.trim();
	let days: number | null = null;
	if (raw !== "") {
		const parsed = Number(raw);
		if (!Number.isInteger(parsed) || parsed < 0) {
			return;
		}
		days = parsed;
	}
	interventionResiduals.value[id] = raw;
	await runInterventionSave(async () => {
		await storeIntervention.update(id, days);
	});
}

onMounted(loadInterventions);
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
